/**
 * Line breaking for the canvas reel, as the browser does it for the CSS reel: greedy wrapping on spaces, forced breaks, and
 * `text-wrap: balance`. Pure functions of a width measure, so the reel's own font measurement can be plugged in — or a fake one
 * in tests.
 */
export interface TextRun { text: string; accent?: boolean }

/** The words of the runs in order, each with its run's accent flag; `null` marks a forced line break. */
export function wordsOf(runs: TextRun[], preLine: boolean): (TextRun | null)[] {
  const out: (TextRun | null)[] = [];
  for (const r of runs) {
    const parts = preLine ? r.text.split("\n") : [r.text];
    parts.forEach((part, i) => {
      if (i) out.push(null);
      for (const w of part.split(/\s+/)) if (w) out.push({ text: w, accent: r.accent });
    });
  }
  return out;
}

/** Greedy word wrap on spaces, as a browser breaks lines: a word wider than the line stands alone; `null` words force a break. */
export function breakLines(words: (TextRun | null)[], maxWidth: number, measure: (s: string) => number): TextRun[][] {
  const space = measure(" ");
  const lines: TextRun[][] = [];
  let line: TextRun[] = [], width = 0;
  const flush = () => { lines.push(line); line = []; width = 0; };
  for (const w of words) {
    if (!w) { flush(); continue; }
    const ww = measure(w.text);
    if (line.length && width + space + ww > maxWidth) flush();
    line.push(w); width += (line.length > 1 ? space : 0) + ww;
  }
  if (line.length || !lines.length) lines.push(line);
  return lines;
}

/** Longest paragraph `text-wrap: balance` still balances; longer ones wrap greedily, as in the browsers. */
const BALANCE_MAX_LINES = 10;

/**
 * CSS `text-wrap: balance`, matched against what Chrome drew for the reel's titles: the greedy line count is kept, and among the
 * ways to break the words into that many lines the one whose lines leave the least room, summed as squared slack over every
 * line (the last one too), wins. Chrome's own choice for "NO KINGDOM / FELL TO / ITS ENEMY" rules out the simpler
 * narrowest-width bisection, which keeps "FELL TO ITS" on one line because "NO KINGDOM" is wider. A forced break ends a
 * paragraph; each paragraph is balanced on its own. A word wider than the line stands alone, as it does when wrapping greedily.
 */
export function balancedLines(words: (TextRun | null)[], maxWidth: number, measure: (s: string) => number): TextRun[][] {
  const greedy = breakLines(words, maxWidth, measure);
  if (greedy.length <= 1 || greedy.length > BALANCE_MAX_LINES) return greedy;
  const space = measure(" ");
  const out: TextRun[][] = [];
  const paragraphs: TextRun[][] = [[]];
  for (const w of words) (w ? paragraphs[paragraphs.length - 1].push(w) : paragraphs.push([]));
  for (const para of paragraphs) {
    const target = breakLines(para, maxWidth, measure).length;
    out.push(...(para.length ? evenLines(para, target, maxWidth, space, measure) : [[]]));
  }
  return out;
}

/** The `count`-line break of `words` with the least total squared slack, by dynamic programming over the line ends. */
function evenLines(words: TextRun[], count: number, maxWidth: number, space: number, measure: (s: string) => number): TextRun[][] {
  const n = words.length;
  const widths = words.map((w) => measure(w.text));
  // width of words i..j-1 as one line
  const lineWidth = (i: number, j: number) => widths.slice(i, j).reduce((s, w) => s + w, 0) + space * (j - i - 1);
  const cost = (i: number, j: number) => { const w = lineWidth(i, j); return w <= maxWidth ? (maxWidth - w) ** 2 : j - i === 1 ? 0 : Infinity; };
  // best[k][j]: least cost breaking the first j words into k lines; from[k][j]: where the last of those lines starts
  const best: number[][] = Array.from({ length: count + 1 }, () => Array<number>(n + 1).fill(Infinity));
  const from: number[][] = Array.from({ length: count + 1 }, () => Array<number>(n + 1).fill(-1));
  best[0][0] = 0;
  for (let k = 1; k <= count; k++) for (let j = k; j <= n; j++) for (let i = k - 1; i < j; i++) {
    if (best[k - 1][i] === Infinity) continue;
    const c = best[k - 1][i] + cost(i, j);
    if (c < best[k][j]) { best[k][j] = c; from[k][j] = i; }
  }
  if (best[count][n] === Infinity) return breakLines(words, maxWidth, measure);
  const lines: TextRun[][] = [];
  for (let k = count, j = n; k > 0; k--) { const i = from[k][j]; lines.unshift(words.slice(i, j)); j = i; }
  return lines;
}
